// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

// AI reference grades: OpenAI and Claude models grade the card on screen from each imaging
// source (cvat/apps/engine/ai_grades.py). Labels, the order of the rows and how numbers read.

import {
    AiGradeProvider, AiGradeResult, AiGradeSource, AiGradesState, AiModelsState, AiSideGrades,
} from 'reducers/interfaces';

export const AI_GRADE_SOURCES: AiGradeSource[] = ['luminance', 'height', 'camera'];
// the pickers' order, as the rows run: Claude models first
export const AI_GRADE_PROVIDERS: AiGradeProvider[] = ['claude', 'openai'];

export const AI_GRADE_SOURCE_LABELS: Record<AiGradeSource, { title: string; detail: string; file: string }> = {
    luminance: { title: 'Laser scan', detail: 'Luminance PNG', file: 'laser scan' },
    height: { title: 'Raw laser file', detail: 'Height map', file: 'height map' },
    camera: { title: 'HD camera', detail: 'Camera PNG', file: 'camera photo' },
};

export const AI_GRADE_PROVIDER_LABELS: Record<AiGradeProvider, string> = {
    openai: 'OpenAI',
    claude: 'Claude',
};

// The AI grades of no card: the store's start, and what the panel shows until a card's are in
export const EMPTY_AI_GRADES: AiGradesState = {
    certificateId: null,
    loaded: false,
    error: null,
    results: [],
    busy: false,
    canGenerate: false,
    spentUsd: 0,
    calls: 0,
    unpricedCalls: 0,
    starting: [],
};

const capitalized = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

const isWord = (part: string | undefined): boolean => !!part && /^[a-z]+$/.test(part);
const isNumber = (part: string | undefined): boolean => !!part && /^\d+$/.test(part);

// claude-opus-5-5 → Opus 5.5, claude-sonnet-5-20260514 → Sonnet 5, gpt-6.1-sol → GPT-6.1 Sol;
// other ids read as they are
export function shortModelName(model: string): string {
    const parts = model.split('-');
    if (parts[0] === 'claude' && isWord(parts[1]) && isNumber(parts[2])) {
        // claude-<family>-<major>[-<minor>][-<date>]
        const minor = isNumber(parts[3]) && parts[3].length <= 2 ? `.${parts[3]}` : '';
        return `${capitalized(parts[1])} ${parts[2]}${minor}`;
    }
    if (parts.length === 3 && parts[0] === 'gpt' && /^\d[\d.]*$/.test(parts[1]) && isWord(parts[2])) {
        return `GPT-${parts[1]} ${capitalized(parts[2])}`;
    }
    return model;
}

// a fraction of a cent keeps its first digit: $0.002, not $0.00
export const formatCost = (value: number): string => (
    value > 0 && value < 0.01 ? `$${value.toFixed(3)}` : `$${value.toFixed(2)}`
);

export const formatTokens = (count: number): string => (
    count >= 1000 ? `${Number((count / 1000).toFixed(1))}k` : `${count}`
);

export const isAiGradeBusy = (result: AiGradeResult): boolean => (
    result.status === 'pending' || result.status === 'running'
);

// The first run's models in their order, then the models run by hand, by name
export function sortAiGradeResults(results: AiGradeResult[], aiModels: AiModelsState): AiGradeResult[] {
    const rank = (result: AiGradeResult): number => {
        const index = aiModels.autoModels.findIndex((entry) => entry.model === result.model);
        return index === -1 ? aiModels.autoModels.length : index;
    };
    return [...results].sort((a, b) => rank(a) - rank(b) || a.model.localeCompare(b.model));
}

// the server's snake_case result, as the panel keeps it
export function toAiGradeResult(raw: any): AiGradeResult {
    const side = (value: any): AiSideGrades => ({
        centering: Number(value.centering),
        corners: Number(value.corners),
        edges: Number(value.edges),
        surface: Number(value.surface),
        centeringRatio: {
            leftRight: value.centering_ratio?.left_right ?? '',
            topBottom: value.centering_ratio?.top_bottom ?? '',
        },
        notes: value.notes ?? '',
    });
    const number = (value: any): number | null => (value === null || value === undefined ? null : Number(value));

    return {
        source: raw.source,
        provider: raw.provider,
        model: raw.model,
        status: raw.status,
        error: raw.error ?? null,
        answeredBy: raw.answered_by ?? null,
        effort: raw.effort ?? null,
        grades: raw.grades ? { front: side(raw.grades.front), back: side(raw.grades.back) } : null,
        frontTotal: number(raw.front_total),
        backTotal: number(raw.back_total),
        overallUncapped: number(raw.overall_uncapped),
        overall: number(raw.overall),
        usage: raw.input_tokens === null || raw.input_tokens === undefined ? null : {
            inputTokens: Number(raw.input_tokens),
            cachedInputTokens: Number(raw.cached_input_tokens ?? 0),
            outputTokens: Number(raw.output_tokens ?? 0),
        },
        costUsd: number(raw.cost_usd),
        elapsedMs: number(raw.elapsed_ms),
        promptVersion: number(raw.prompt_version),
        gradedAt: raw.graded_at ?? null,
        gradedBy: raw.graded_by ?? null,
    };
}
